import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import { MdCardGiftcard, MdConfirmationNumber } from "react-icons/md";
import toast from "react-hot-toast";
import axios from "axios";
import { BASE_API_URL } from "../helpers/Config";
import { useNavigate } from "react-router-dom";
import { TriggerBalanceUpdate } from "../helpers/TriggerBalanceUpdate";
import moment from "moment";
import { getPageStyles, MOTION } from "../helpers/pageTheme";

const SectionCard = ({ styles, children }) => (
    <div className="p-3" style={styles.card}>
        {children}
    </div>
);

const CouponScreen = ({ theme = "dark" }) => {
    const [couponCode, setCouponCode] = useState("");
    const [claimedCoupons, setClaimedCoupons] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const isDark = theme === "dark";
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);

    const FetchClaimCoupons = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/payment/redeem-history`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;
            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.clear("a5srf4wer7w1cd");
                    navigate("/login");
                } else toast.error(data?.error);
            }
            if (data?.data) setClaimedCoupons(data.data);
        } catch (error) {
            toast.error("Technical error");
            console.log(error);
        }
    };

    const handleClaimCoupon = async (e) => {
        e.preventDefault();
        if (!couponCode.trim()) {
            toast.error("Please enter a coupon code");
            return;
        }
        setIsLoading(true);
        try {
            const response = await axios.post(`${BASE_API_URL}/payment/coupon-claim`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                coupon_code: couponCode,
            });
            const data = response?.data;
            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.clear("a5srf4wer7w1cd");
                    navigate("/login");
                } else toast.error(data?.error);
            }
            if (data?.msg) {
                toast.success(data?.msg);
                FetchClaimCoupons();
            }
            if (data?.balance) {
                TriggerBalanceUpdate({ balance: data.balance, usdt: data.usdt });
            }
        } catch (error) {
            toast.error("Technical error");
            console.log(error);
        } finally {
            setCouponCode("");
            setIsLoading(false);
        }
    };

    useEffect(() => {
        FetchClaimCoupons();
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="px-3 py-3 overflow-auto"
            style={styles.page}
        >
            <motion.div variants={MOTION.container} initial="hidden" animate="visible" className="d-flex flex-column gap-3">
                <motion.div variants={MOTION.item} style={styles.hero(styles.heroTeal)}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <MdCardGiftcard size={28} />
                        <h5 className="fw-bold mb-0">Coupons</h5>
                    </div>
                    <p className="mb-0 small opacity-75">Enter a code to claim rewards</p>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles}>
                        <p className="mb-3" style={styles.sectionTitle}>
                            Redeem code
                        </p>
                        <form onSubmit={handleClaimCoupon}>
                            <div className="d-flex gap-2">
                                <input
                                    type="text"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    placeholder="e.g. WELCOME10"
                                    disabled={isLoading}
                                    className="form-control flex-grow-1"
                                    style={{ ...styles.input, fontSize: "0.9rem" }}
                                />
                                <motion.button
                                    type="submit"
                                    className="btn"
                                    disabled={isLoading || !couponCode}
                                    whileTap={{ scale: 0.97 }}
                                    style={{
                                        ...styles.submitBtn,
                                        padding: "10px 20px",
                                        opacity: couponCode ? 1 : 0.6,
                                    }}
                                >
                                    {isLoading ? (
                                        <span className="spinner-border spinner-border-sm" />
                                    ) : (
                                        "Claim"
                                    )}
                                </motion.button>
                            </div>
                        </form>
                    </SectionCard>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles}>
                        <p className="mb-3" style={styles.sectionTitle}>
                            Your claimed coupons
                        </p>
                        <AnimatePresence>
                            {claimedCoupons.length === 0 ? (
                                <div style={styles.empty}>No coupons claimed yet</div>
                            ) : (
                                claimedCoupons.map((coupon, index) => (
                                    <motion.div
                                        key={coupon.id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="py-3"
                                        style={{
                                            borderBottom:
                                                index === claimedCoupons.length - 1
                                                    ? "none"
                                                    : `1px solid ${styles.listBorder}`,
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                            <div className="d-flex align-items-center gap-2 min-w-0">
                                                <span
                                                    className="fw-bold"
                                                    style={{
                                                        color: "#22c55e",
                                                        background: isDark ? "rgba(34,197,94,0.12)" : "#dcfce7",
                                                        padding: "6px 10px",
                                                        borderRadius: "8px",
                                                        fontSize: "0.9rem",
                                                    }}
                                                >
                                                    +{coupon.amount}
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="fw-semibold text-truncate">{coupon.code}</div>
                                                    <small style={{ color: styles.textMuted }}>
                                                        {moment(coupon.claim_date).format("DD MMM YYYY")}
                                                    </small>
                                                </div>
                                            </div>
                                            <span className="badge" style={{ background: isDark ? "rgba(34,197,94,0.15)" : "#dcfce7", color: "#22c55e" }}>
                                                Claimed
                                            </span>
                                        </div>
                                        <div
                                            className="d-flex align-items-center gap-1 font-monospace small px-2 py-1"
                                            style={{ ...styles.input, fontSize: "0.75rem" }}
                                        >
                                            <MdConfirmationNumber size={14} style={{ color: styles.textMuted }} />
                                            {coupon.coupon_code}
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </AnimatePresence>
                    </SectionCard>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default CouponScreen;
